import React from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCoins } from '@fortawesome/pro-light-svg-icons/faCoins';
import { faClock } from '@fortawesome/pro-light-svg-icons/faClock';
import { faCheck } from '@fortawesome/pro-light-svg-icons/faCheck';
import { faLock } from '@fortawesome/pro-light-svg-icons/faLock';
import type { BrowseRowState } from 'fym-shared/src/music24/browseSearch';
import { TypedMemo } from '../../../common/types';
import { useBrowseRowContext } from '../BrowseContext';
import { browseMessages as messages } from '../browseMessages';
/* eslint-disable css-modules/no-unused-class -- a shared stylesheet; each part uses its own slice of it */
import styles from './BrowseRows.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface BrowseCostChipProps {
  state: BrowseRowState;
  className?: string;
}

/**
 * What it costs to open a row (design a1): "1 credit", "Opened today", "Followed", "Free" for a
 * playlist. With one credit left it says "Last credit" in amber (b7); with none, "No credits" (b5).
 */
const BrowseCostChipComponent: React.FC<BrowseCostChipProps> = ({ state, className }) => {
  const intl = useIntl();
  const { outOfCredits, lastCredit } = useBrowseRowContext();

  if (state === 'followed' || state === 'openedToday' || state === 'free') {
    const label = {
      followed: messages.costFollowed,
      openedToday: messages.costOpenedToday,
      free: messages.costFree,
    }[state];
    return (
      <span className={clsx(styles.cost, styles.costFree, className)} data-testid={`browse-cost-${state}`}>
        <FontAwesomeIcon icon={state === 'openedToday' ? faClock : faCheck} />
        {intl.formatMessage(label)}
      </span>
    );
  }
  if (outOfCredits) {
    return (
      <span className={clsx(styles.cost, styles.costOut, className)} data-testid="browse-cost-none">
        <FontAwesomeIcon icon={faLock} />
        {intl.formatMessage(messages.costNoCredits)}
      </span>
    );
  }
  return (
    <span
      className={clsx(styles.cost, lastCredit && styles.costWarn, className)}
      data-testid={lastCredit ? 'browse-cost-last' : 'browse-cost-credit'}
    >
      <FontAwesomeIcon icon={faCoins} />
      {intl.formatMessage(lastCredit ? messages.costLastCredit : messages.costCredit)}
    </span>
  );
};

export const BrowseCostChip = TypedMemo(BrowseCostChipComponent);
